/**
 * SecureEmbed Energy Portal - Related Pages Constants
 *
 * Maps each portal page route to its related pages with pre-filled context
 * and navigation type indicators. Used by RelatedPagesCard component.
 * Addresses bug #289 — Related Pages navigation indicators.
 */

export type NavigationType = 'same-tab' | 'new-tab' | 'modal';

export interface RelatedPage {
  route: string;
  label: string;
  description: string;
  iconName: string;
  navigationType: NavigationType;
  prefillParams?: Record<string, string>;
  external?: boolean;
}

// ---------------------------------------------------------------------------
// Related pages by source route
// ---------------------------------------------------------------------------
export const RELATED_PAGES: Record<string, RelatedPage[]> = {
  '/': [
    {
      route: '/search',
      label: 'Semantic Search',
      description: 'Search across all energy datastores using natural language queries.',
      iconName: 'Search',
      navigationType: 'same-tab',
    },
    {
      route: '/export',
      label: 'Data Export',
      description: 'Export datastore records in CSV, JSON, or Excel format.',
      iconName: 'Download',
      navigationType: 'same-tab',
    },
    {
      route: '/partners',
      label: 'Partners & Organisations',
      description: 'Browse energy organisations and partnership networks.',
      iconName: 'Building2',
      navigationType: 'same-tab',
    },
    {
      route: 'https://aemo.com.au',
      label: 'AEMO Website',
      description: 'Australian Energy Market Operator — official data and publications.',
      iconName: 'ExternalLink',
      navigationType: 'new-tab',
      external: true,
    },
  ],

  '/search': [
    {
      route: '/',
      label: 'DataStores',
      description: 'Browse all energy datastores and their record counts.',
      iconName: 'Database',
      navigationType: 'same-tab',
    },
    {
      route: '/insights',
      label: 'AI Insights',
      description: 'AI-generated insights from your search results and data patterns.',
      iconName: 'Lightbulb',
      navigationType: 'same-tab',
    },
    {
      route: '/export',
      label: 'Export Results',
      description: 'Export your search results for offline analysis.',
      iconName: 'Download',
      navigationType: 'same-tab',
      prefillParams: { source: 'search' },
    },
    {
      route: '/provenance',
      label: 'Data Provenance',
      description: 'Track the origin and lineage of search results.',
      iconName: 'GitBranch',
      navigationType: 'same-tab',
    },
  ],

  '/insights': [
    {
      route: '/search',
      label: 'Semantic Search',
      description: 'Run targeted searches to explore insights further.',
      iconName: 'Search',
      navigationType: 'same-tab',
    },
    {
      route: '/',
      label: 'DataStores',
      description: 'Browse the underlying datastores powering insights.',
      iconName: 'Database',
      navigationType: 'same-tab',
    },
    {
      route: '/reports',
      label: 'Reports',
      description: 'Generate structured reports from insight findings.',
      iconName: 'FileText',
      navigationType: 'same-tab',
    },
    {
      route: '/research',
      label: 'Research References',
      description: 'Academic and industry research supporting the analysis.',
      iconName: 'BookOpen',
      navigationType: 'same-tab',
    },
  ],

  '/partners': [
    {
      route: '/',
      label: 'DataStores',
      description: 'View datastores associated with partner organisations.',
      iconName: 'Database',
      navigationType: 'same-tab',
    },
    {
      route: '/search',
      label: 'Search by Organisation',
      description: 'Search records filtered by a specific organisation.',
      iconName: 'Search',
      navigationType: 'same-tab',
      prefillParams: { filter: 'organisation' },
    },
    {
      route: 'https://www.aer.gov.au',
      label: 'AER Website',
      description: 'Australian Energy Regulator — regulatory decisions and compliance.',
      iconName: 'ExternalLink',
      navigationType: 'new-tab',
      external: true,
    },
  ],

  '/provenance': [
    {
      route: '/search',
      label: 'Semantic Search',
      description: 'Search for records to trace their provenance chain.',
      iconName: 'Search',
      navigationType: 'same-tab',
    },
    {
      route: '/research',
      label: 'Research References',
      description: 'View academic sources referenced in provenance chains.',
      iconName: 'BookOpen',
      navigationType: 'same-tab',
    },
    {
      route: '/insights',
      label: 'AI Insights',
      description: 'Explore AI-derived insights with full provenance tracking.',
      iconName: 'Lightbulb',
      navigationType: 'same-tab',
    },
  ],

  '/reports': [
    {
      route: '/export',
      label: 'Data Export',
      description: 'Export report data in multiple formats.',
      iconName: 'Download',
      navigationType: 'same-tab',
    },
    {
      route: '/insights',
      label: 'AI Insights',
      description: 'View AI-generated insights that informed this report.',
      iconName: 'Lightbulb',
      navigationType: 'same-tab',
    },
    {
      route: '/search',
      label: 'Semantic Search',
      description: 'Run follow-up searches based on report findings.',
      iconName: 'Search',
      navigationType: 'same-tab',
    },
  ],

  '/export': [
    {
      route: '/reports',
      label: 'Reports',
      description: 'Generate structured reports before exporting.',
      iconName: 'FileText',
      navigationType: 'same-tab',
    },
    {
      route: '/search',
      label: 'Semantic Search',
      description: 'Refine your data selection before export.',
      iconName: 'Search',
      navigationType: 'same-tab',
    },
    {
      route: '/',
      label: 'DataStores',
      description: 'Browse available datastores for export.',
      iconName: 'Database',
      navigationType: 'same-tab',
    },
  ],
};

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------
export const getRelatedPages = (route: string): RelatedPage[] => {
  return RELATED_PAGES[route] || [];
};
